export type VeeraPersistedMessage = {
  session_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  thread_title?: string | null;
};

export type VeeraScopedPersistedMessage = VeeraPersistedMessage & {
  context_type: "global" | "leader" | "coach";
  leader_id: string | null;
};

export function filterHomogeneousVeeraSessions<
  TMessage extends VeeraScopedPersistedMessage,
>(messages: TMessage[]): TMessage[] {
  const sessions = new Map<string, TMessage[]>();
  for (const message of messages) {
    const rows = sessions.get(message.session_id);
    if (rows) rows.push(message);
    else sessions.set(message.session_id, [message]);
  }

  const safe: TMessage[] = [];
  for (const rows of sessions.values()) {
    const first = rows[0];
    const firstLeaderId = first.context_type === "leader" ? first.leader_id : null;
    const firstIsValid = first.context_type === "leader"
      ? Boolean(firstLeaderId)
      : first.leader_id === null;
    if (!firstIsValid) continue;
    const isHomogeneous = rows.every((row) => (
      row.context_type === first.context_type
      && (row.context_type === "leader" ? row.leader_id === firstLeaderId : row.leader_id === null)
    ));
    if (isHomogeneous) safe.push(...rows);
  }
  return safe;
}

export type VeeraThreadSummary = {
  id: string;
  title: string;
  preview: string;
  updatedAt: string;
};

const VEERA_THREAD_TITLE_STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "about",
  "are",
  "across",
  "as",
  "at",
  "am",
  "be",
  "because",
  "been",
  "but",
  "by",
  "can",
  "could",
  "did",
  "do",
  "does",
  "for",
  "from",
  "had",
  "has",
  "have",
  "how",
  "i",
  "in",
  "is",
  "it",
  "me",
  "my",
  "of",
  "on",
  "or",
  "should",
  "that",
  "the",
  "this",
  "to",
  "was",
  "what",
  "when",
  "where",
  "why",
  "with",
  "would",
  "you",
  "your",
]);

type BuildVeeraThreadsOptions<
  TMessage extends VeeraPersistedMessage,
  TThread extends VeeraThreadSummary,
> = {
  fallbackTitle: string | ((firstMessage: TMessage | undefined) => string);
  titleLimit: number;
  previewLimit: number;
  mapThread?: (
    summary: VeeraThreadSummary,
    latestMessage: TMessage,
    sortedMessages: TMessage[],
  ) => TThread;
};

const truncateVeeraThreadText = (value: string, limit: number) => (
  value.length > limit ? `${value.slice(0, limit)}...` : value
);

export const createVeeraThreadStoreKey = (
  surface: "coach" | "leader",
  userId?: string,
) => `veera-${surface}-threads-${userId || "anon"}`;

export const createVeeraDeletedThreadStoreKey = (
  surface: "coach" | "leader",
  userId?: string,
) => `veera-${surface}-deleted-threads-${userId || "anon"}`;

const titleCaseWord = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const normalizeVeeraTitleWords = (value: string) => value
  .replace(/[^\p{L}\p{N}'-]+/gu, " ")
  .trim()
  .split(/\s+/)
  .filter(Boolean);

export const readPersistedVeeraDeletedThreadIds = (
  surface: "coach" | "leader",
  userId?: string,
) => {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(createVeeraDeletedThreadStoreKey(surface, userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
};

export const persistVeeraDeletedThreadId = (
  surface: "coach" | "leader",
  userId: string | undefined,
  threadId: string,
) => {
  if (typeof window === "undefined") return;

  const existing = readPersistedVeeraDeletedThreadIds(surface, userId);
  if (existing.includes(threadId)) return;
  window.localStorage.setItem(
    createVeeraDeletedThreadStoreKey(surface, userId),
    JSON.stringify([...existing, threadId]),
  );
};

export const buildGeneratedVeeraThreadTitle = (
  value: string | null | undefined,
  fallbackTitle: string,
  maxCharacters: number,
) => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return fallbackTitle;

  const words = normalizeVeeraTitleWords(normalizedValue);
  if (words.length === 0) return fallbackTitle;

  const contentWords = words.filter((word) => !VEERA_THREAD_TITLE_STOPWORDS.has(word.toLowerCase()));
  const candidateWords = contentWords.length >= 3
    ? contentWords.slice(0, 4)
    : words.filter((word, index) => !(index === 0 && VEERA_THREAD_TITLE_STOPWORDS.has(word.toLowerCase()))).slice(0, 4);
  const resolvedWords = (candidateWords.length > 0 ? candidateWords : words.slice(0, 4)).map(titleCaseWord);

  return truncateVeeraThreadText(resolvedWords.join(" "), maxCharacters);
};

export const formatVeeraThreadTimestamp = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Now";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export function buildVeeraThreadsFromMessages<
  TMessage extends VeeraPersistedMessage,
  TThread extends VeeraThreadSummary = VeeraThreadSummary,
>(
  messages: TMessage[],
  options: BuildVeeraThreadsOptions<TMessage, TThread>,
): TThread[] {
  const grouped = new Map<string, TMessage[]>();

  for (const message of messages) {
    const existing = grouped.get(message.session_id);
    if (existing) {
      existing.push(message);
      continue;
    }
    grouped.set(message.session_id, [message]);
  }

  return Array.from(grouped.entries())
    .map(([sessionId, sessionMessages]) => {
      const sorted = [...sessionMessages].sort((a, b) => (
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ));
      const latestMessage = sorted[sorted.length - 1];
      const resolvedFallbackTitle = typeof options.fallbackTitle === "function"
        ? options.fallbackTitle(sorted[0])
        : options.fallbackTitle;
      const metadataTitle = [...sorted]
        .reverse()
        .find((message) => typeof message.thread_title === "string" && message.thread_title.trim().length > 0)
        ?.thread_title
        ?.trim();
      const firstUserMessage = sorted.find((message) => message.role === "user")?.content || resolvedFallbackTitle;
      const summary: VeeraThreadSummary = {
        id: sessionId,
        title: buildGeneratedVeeraThreadTitle(metadataTitle || firstUserMessage, resolvedFallbackTitle, options.titleLimit),
        preview: truncateVeeraThreadText(latestMessage.content, options.previewLimit),
        updatedAt: latestMessage.created_at,
      };

      if (options.mapThread) {
        return options.mapThread(summary, latestMessage, sorted);
      }

      return summary as TThread;
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}
